import { useState, useEffect } from 'react';
import SearchableSelect from './SearchableSelect';
import useStatesCities from '../../hooks/useStatesCities';

/**
 * Two side-by-side searchable dropdowns for State and City, backed by the
 * /api/locations master data. City options are filtered by the selected
 * State; both fields accept free-text values that aren't in the list.
 *
 * Props:
 *   state, city               — current string values
 *   onStateChange, onCityChange — (value: string) => void
 *   disabled                   — disables both fields
 *   required                   — marks both labels with '*'
 *   formGroupClassName         — wrapper class for each field (default 'form-group')
 *   labelClassName             — class for each <label> (default '')
 */
export default function StateCitySelect({
  state,
  city,
  onStateChange,
  onCityChange,
  disabled = false,
  required = false,
  formGroupClassName = 'form-group',
  labelClassName = '',
}) {
  const { states, getCitiesForState, loading } = useStatesCities();
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const selectedState = states.find((s) => s.name === state);

  useEffect(() => {
    if (!selectedState) {
      setCities([]);
      return;
    }
    let active = true;
    setCitiesLoading(true);
    getCitiesForState(selectedState.id)
      .then((list) => { if (active) setCities(list); })
      .finally(() => { if (active) setCitiesLoading(false); });
    return () => { active = false; };
  }, [selectedState, getCitiesForState]);

  const stateOptions = states.map((s) => ({ value: s.name, label: s.name }));
  const cityOptions = cities.map((c) => ({ value: c.name, label: c.name }));

  return (
    <>
      <div className={formGroupClassName}>
        <label className={labelClassName}>State{required && ' *'}</label>
        <SearchableSelect
          value={state}
          onChange={onStateChange}
          options={stateOptions}
          placeholder="Select state"
          searchPlaceholder="Search state…"
          disabled={disabled || loading}
          allowCustom
        />
      </div>
      <div className={formGroupClassName}>
        <label className={labelClassName}>City{required && ' *'}</label>
        <SearchableSelect
          value={city}
          onChange={onCityChange}
          options={cityOptions}
          placeholder="Select city"
          searchPlaceholder="Search city…"
          disabled={disabled || citiesLoading}
          allowCustom
        />
      </div>
    </>
  );
}
